/**
 * School-owned phase identity and eligibility. Generated unchanged for mobile
 * and as CommonJS for Functions. Callers supply trusted school-scoped records;
 * a staff view/filter is deliberately not an input to learner resolution.
 */

export const PHASE_CONTEXT_VERSION = 1;
export const WHOLE_SCHOOL_APPLICABILITY = Object.freeze({
  mode: 'whole-school', phaseIds: Object.freeze([]),
});

const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const identifier = value => typeof value === 'string' && /^[^/\s]{1,128}$/.test(value)
  && !['.', '..'].includes(value);
const active = phase => phase && phase.retired !== true && phase.active !== false;

export function validateSchoolPhases(phases) {
  if (!Array.isArray(phases)) throw new Error('Phases must be a list.');
  const ids = new Set();
  const names = new Set();
  for (const phase of phases) {
    if (!identifier(phase?.id) || ids.has(phase.id)) {
      throw new Error('Each phase needs a unique, stable identity.');
    }
    if (typeof phase.name !== 'string' || !phase.name.trim() || phase.name.length > 100) {
      throw new Error('Give each phase a name of at most 100 characters.');
    }
    const name = phase.name.trim().toLocaleLowerCase();
    if (active(phase) && names.has(name)) throw new Error('A phase with this name already exists.');
    if (!Number.isInteger(phase.order) || phase.order < 0) {
      throw new Error('Phase order must be a non-negative whole number.');
    }
    ids.add(phase.id);
    if (active(phase)) names.add(name);
  }
  return true;
}

export function normalizePhaseApplicability(value, { phases, allowRetired = false } = {}) {
  // Absent configuration preserves the school's existing unrestricted behavior.
  if (value == null) return { mode: 'whole-school', phaseIds: [] };
  if (typeof value !== 'object' || Array.isArray(value)
    || Object.keys(value).some(key => !['mode', 'phaseIds'].includes(key))) {
    throw new Error('Choose Whole school or Selected phases.');
  }
  if (!['whole-school', 'selected-phases'].includes(value.mode)) {
    throw new Error('Choose Whole school or Selected phases.');
  }
  const ids = value.phaseIds ?? [];
  if (!Array.isArray(ids) || ids.some(id => !identifier(id)) || new Set(ids).size !== ids.length) {
    throw new Error('Choose valid, distinct school phases.');
  }
  if (value.mode === 'whole-school' && ids.length) {
    throw new Error('Whole school cannot also select individual phases.');
  }
  if (value.mode === 'selected-phases' && !ids.length) {
    throw new Error('Choose at least one phase.');
  }
  if (phases) {
    const available = new Set(phases.filter(phase => allowRetired || active(phase)).map(phase => phase.id));
    if (ids.some(id => !available.has(id))) throw new Error('A selected phase is unavailable in this school.');
  }
  return { mode: value.mode, phaseIds: [...ids].sort() };
}

/** Validate an explicit child selection against the Domain's participation. */
export function assertPhaseApplicabilityWithin(child, parent, options) {
  const childValue = normalizePhaseApplicability(child, options);
  const parentValue = normalizePhaseApplicability(parent, options);
  if (childValue.mode === 'selected-phases' && parentValue.mode === 'selected-phases'
    && childValue.phaseIds.some(id => !parentValue.phaseIds.includes(id))) {
    throw new Error('Selected phases must also participate in this Domain.');
  }
  return childValue;
}

/** null denotes unrestricted eligibility, including explicitly unassigned grades. */
function effectiveIds(domain, item) {
  const parent = normalizePhaseApplicability(domain);
  const child = normalizePhaseApplicability(item);
  if (parent.mode === 'whole-school') return child.mode === 'whole-school' ? null : child.phaseIds;
  return child.mode === 'whole-school' ? parent.phaseIds
    : child.phaseIds.filter(id => parent.phaseIds.includes(id));
}

/** A rule must never be capable of producing an unavailable Action. */
export function assertRuleActionPhaseCoverage({ domain, rule, action, phases, subjectPhaseIds }) {
  const options = { phases };
  assertPhaseApplicabilityWithin(rule, domain, options);
  assertPhaseApplicabilityWithin(action, domain, options);
  let sources = effectiveIds(domain, rule);
  const targets = effectiveIds(domain, action);
  // Existing grade/class targeting can narrow subjects. Supply only a trusted,
  // complete resolution; null means an unassigned subject, not an unknown one.
  if (subjectPhaseIds !== undefined) {
    if (!Array.isArray(subjectPhaseIds) || subjectPhaseIds.some(id => id !== null && !identifier(id))) {
      throw new Error('Resolve the rule’s existing learner targeting before saving.');
    }
    sources = subjectPhaseIds.filter(id => sources === null || sources.includes(id));
  }
  if (targets !== null && (sources === null || sources.some(id => !targets.includes(id)))) {
    throw new Error('The resulting action must be available to every learner this rule can affect.');
  }
  return true;
}

export function phaseApplicabilityAllows(applicability, context, domainApplicability) {
  const ids = effectiveIds(domainApplicability, applicability);
  if (ids === null) return true;
  return context?.version === PHASE_CONTEXT_VERSION && context.status === 'known'
    && typeof context.phaseId === 'string' && ids.includes(context.phaseId);
}

function unresolved(schoolId, reason, extra = {}) {
  return { version: PHASE_CONTEXT_VERSION, schoolId, status: 'unresolved', phaseId: null, reason, ...extra };
}

/** People lifecycle flags; absence preserves legacy active-learner compatibility. */
export function isInactivePhaseLearner(learner) {
  return learner?.active === false || learner?.isActive === false || learner?.graduated === true
    || ['inactive', 'graduated', 'transferred', 'withdrawn'].includes(String(learner?.status || '').trim().toLowerCase());
}

/**
 * Resolve from canonical grade references, never numeric grade/name inference.
 * Existing legacy labels need an explicitly reviewed reference mapping first.
 */
export function resolveLearnerPhaseContext({ schoolId, learner, grades = [], classes = [], phases = [], includeInactiveForReview = false }) {
  if (!identifier(schoolId)) throw new Error('A school is required to resolve a learner’s phase.');
  if (!learner || (learner.schoolId && learner.schoolId !== schoolId)) {
    return unresolved(schoolId, 'learner-unavailable');
  }
  // People retains grade facts when archiving a learner. They are available to
  // explicit migration review, but are not current operational membership.
  if (!includeInactiveForReview && isInactivePhaseLearner(learner)) {
    return unresolved(schoolId, 'learner-inactive');
  }
  const schoolClass = learner.classId ? classes.find(item => item.id === learner.classId) : null;
  if (learner.classId && (!schoolClass || (schoolClass.schoolId && schoolClass.schoolId !== schoolId))) {
    return unresolved(schoolId, 'class-unavailable');
  }
  const gradeById = new Map(grades.filter(grade => !grade.schoolId || grade.schoolId === schoolId)
    .map(grade => [grade.id, grade]));
  const classGradeId = schoolClass?.gradeId
    || (gradeById.has(schoolClass?.grade) ? schoolClass.grade : null);
  if (learner.gradeId && classGradeId && learner.gradeId !== classGradeId) {
    return unresolved(schoolId, 'conflicting-grade-references');
  }
  if (schoolClass && !classGradeId) return unresolved(schoolId, 'class-grade-unresolved');
  const gradeId = classGradeId || learner.gradeId;
  const grade = gradeById.get(gradeId);
  if (!grade) return unresolved(schoolId, 'grade-unavailable');
  const identity = { gradeId: grade.id, gradeName: grade.name || '', classId: schoolClass?.id || null };
  if (!own(grade, 'phaseId')) {
    return unresolved(schoolId, 'legacy-grade-phase', identity);
  }
  if (grade.phaseId === null) {
    return { version: PHASE_CONTEXT_VERSION, schoolId, status: 'unassigned', phaseId: null, ...identity };
  }
  const phase = phases.find(item => item.id === grade.phaseId && (!item.schoolId || item.schoolId === schoolId));
  if (!active(phase)) return unresolved(schoolId, 'phase-unavailable', identity);
  return {
    version: PHASE_CONTEXT_VERSION, schoolId, status: 'known',
    phaseId: phase.id, phaseName: phase.name, ...identity,
  };
}

/** Historical reads never derive a missing snapshot from today's enrolment. */
export function recordedPhaseContext(record, schoolId) {
  const context = record?.phaseContext;
  if (!context || context.version !== PHASE_CONTEXT_VERSION || context.schoolId !== schoolId
    || !['known', 'unassigned', 'unresolved'].includes(context.status)
    || (context.status === 'known' && !identifier(context.phaseId))
    || (context.status !== 'known' && context.phaseId !== null)) {
    return unresolved(schoolId, 'historical-context-unavailable');
  }
  return { ...context };
}

/** Apply after the existing permission/scope check, to complete result sets. */
export function phaseFilterMatchesRecord(record, selectedPhaseIds, schoolId) {
  if (!selectedPhaseIds?.length) return true;
  const context = recordedPhaseContext(record, schoolId);
  return context.status === 'known' && selectedPhaseIds.includes(context.phaseId);
}
